import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopNav from "@/components/TopNav";
import StatCard from "@/components/StatCard";
import PerformanceChart from "@/components/PerformanceChart";
import VideoList from "@/components/VideoList";
import AISuggestions from "@/components/AISuggestions";
import ChannelAnalysis from "@/components/ChannelAnalysis";
import ThumbnailInsights from "@/components/ThumbnailInsights";
import ChannelsOverview from "@/components/ChannelsOverview";
import CompareChannels from "@/components/CompareChannels";
import FSPPanel from "@/components/FSPPanel";
import CompetitorRadar from "@/components/CompetitorRadar";
import UsageDashboard from "@/components/UsageDashboard";
import AdminHome from "@/components/AdminHome";
import { formatFull, formatDate } from "@/lib/format";
import {
  UsersThreeIcon, EyeIcon, FilmSlateIcon, PlugIcon, MagnifyingGlassIcon, CircleNotchIcon,
  GaugeIcon, SquaresFourIcon, ScalesIcon, RobotIcon, CrosshairIcon, ChartBarIcon,
} from "@phosphor-icons/react";

const ConnectPanel = ({ onConnect, connecting, onCancel }) => {
  const [query, setQuery] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (query.trim()) onConnect(query.trim());
  };
  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {onCancel && (
            <button data-testid="cancel-add-channel" onClick={onCancel}
              className="mb-4 font-mono text-[11px] uppercase tracking-wider text-[#A0A0AB] hover:text-[#16C784] transition-colors duration-200">
              ← Back to dashboard
            </button>
          )}
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#A3E635] mb-4">// Link A Channel</div>
          <h1 className="font-rajdhani font-bold text-4xl md:text-5xl tracking-tight text-white text-glow">
            CONNECT YOUR<br /><span className="text-[#16C784]">YOUTUBE CHANNEL</span>
          </h1>
          <p className="mt-6 text-[#A0A0AB] max-w-md leading-relaxed">
            Paste your channel URL or @handle to pull live stats, uploads and per-video telemetry into your dashboard.
          </p>
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
            <input
              data-testid="channel-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="@MrBeast  or  youtube.com/@channel"
              className="flex-1 bg-[#050505] border border-[#222226] focus:border-[#16C784] focus:ring-1 focus:ring-[#16C784] rounded-none text-white px-4 py-3 font-mono text-sm outline-none transition-colors duration-200"
            />
            <button
              data-testid="connect-button"
              type="submit"
              disabled={connecting}
              className="flex items-center justify-center gap-2 bg-[#16C784] hover:bg-[#0FA968] text-white disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors duration-300 arc-glow"
            >
              {connecting ? <CircleNotchIcon size={16} className="spin-slow" /> : <PlugIcon size={16} weight="fill" />}
              {connecting ? "Linking" : "Connect"}
            </button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            {["@MrBeast", "@mkbhd", "@veritasium"].map((s) => (
              <button
                key={s}
                data-testid={`sample-${s}`}
                onClick={() => setQuery(s)}
                className="font-mono text-[11px] text-[#A0A0AB] border border-[#222226] hover:border-[#16C784]/50 hover:text-white px-2.5 py-1 transition-colors duration-200"
              >
                {s}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
          <img
            src="https://images.unsplash.com/photo-1603481588273-2f908a9a7a1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDk1NzZ8MHwxfHNlYXJjaHw0fHxjb250ZW50JTIwY3JlYXRvciUyMGRhcmslMjBzZXR1cHxlbnwwfHx8fDE3ODY2NDUyOTl8MA&ixlib=rb-4.1.0&q=85"
            alt="Creator setup"
            className="w-full aspect-[4/3] object-cover border border-[#222226] grayscale-[0.3]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 font-mono text-[11px] text-[#16C784] tracking-widest">SYSTEM · STANDBY</div>
        </motion.div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [state, setState] = useState({ loading: true, channel: null, videos: [] });
  const [snapshots, setSnapshots] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [booting, setBooting] = useState(true);
  const [view, setView] = useState("single");
  const [adminConnect, setAdminConnect] = useState(false);
  const [overview, setOverview] = useState({ loading: false, channels: [] });

  const loadOverview = useCallback(async () => {
    setOverview((o) => ({ ...o, loading: true }));
    try {
      const res = await api.get("/youtube/overview");
      setOverview({ loading: false, channels: res.data.channels || [] });
    } catch (e) {
      setOverview({ loading: false, channels: [] });
      toast.error("Could not load channel overview");
    }
  }, []);

  const loadChannelData = useCallback(async (id) => {
    setState({ loading: true, channel: null, videos: [] });
    setSnapshots([]);
    const res = await api.get(`/youtube/channel?channel_id=${id}`);
    setState({ loading: false, channel: res.data.channel, videos: res.data.videos });
    const h = await api.get(`/youtube/history?channel_id=${id}`);
    setSnapshots(h.data.snapshots || []);
  }, []);

  const loadChannels = useCallback(async (preferId) => {
    const res = await api.get("/youtube/channels");
    const list = res.data.channels || [];
    setChannels(list);
    if (!list.length) {
      setActiveId(null);
      setState({ loading: false, channel: null, videos: [] });
      setBooting(false);
      return;
    }
    const has = (id) => id && list.some((c) => c.channel_id === id);
    const stored = (() => { try { return localStorage.getItem("co_active_channel"); } catch { return null; } })();
    const id = has(preferId) ? preferId : has(activeId) ? activeId : has(stored) ? stored : list[0].channel_id;
    setActiveId(id);
    await loadChannelData(id);
    setBooting(false);
  }, [activeId, loadChannelData]);

  // Remember the last selected channel until it's manually changed again.
  useEffect(() => {
    try { if (activeId) localStorage.setItem("co_active_channel", activeId); } catch { /* ignore */ }
  }, [activeId]);

  useEffect(() => { loadChannels(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (view === "overview" || view === "compare") loadOverview();
    /* eslint-disable-next-line */
  }, [view]);

  const openChannel = (id) => {
    setView("single");
    handleSwitch(id);
  };

  const handleConnect = async (query) => {
    setConnecting(true);
    try {
      const res = await api.post("/youtube/connect", { query });
      toast.success("Channel linked");
      setAdding(false);
      await loadChannels(res.data.channel_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not connect channel");
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    if (!activeId) return;
    setSyncing(true);
    try {
      await api.post(`/youtube/sync?channel_id=${activeId}`);
      toast.success("Telemetry refreshed");
      await loadChannels(activeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSwitch = async (id) => {
    if (id === activeId) return;
    setActiveId(id);
    setAdding(false);
    try {
      await loadChannelData(id);
    } catch (e) {
      toast.error("Could not load channel");
    }
  };

  const handleDisconnect = async (id) => {
    try {
      await api.get(`/youtube/disconnect?channel_id=${id}`);
      toast.success("Channel removed");
      const nextPrefer = id === activeId ? null : activeId;
      await loadChannels(nextPrefer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove channel");
    }
  };

  const handleAnalyzed = (videoId, analysis) => {
    setState((prev) => ({
      ...prev,
      videos: prev.videos.map((v) => (v.video_id === videoId ? { ...v, ai_analysis: analysis } : v)),
    }));
  };

  const handlePromotedChange = (videoId, promoted) => {
    setState((prev) => ({
      ...prev,
      videos: prev.videos.map((v) => (v.video_id === videoId ? { ...v, promoted } : v)),
    }));
  };

  const { channel, videos } = state;
  const showConnect = channels.length === 0 || adding;
  const showAdminHome = showConnect && user?.is_admin && !user?.impersonating && !adminConnect && !adding;

  // Daily direction: compare current snapshot to the snapshot nearest ~24h earlier.
  const dayDelta = useMemo(() => {
    const snaps = [...(snapshots || [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    if (snaps.length < 2) return null;
    const latest = snaps[snaps.length - 1];
    const latestT = new Date(latest.timestamp).getTime();
    const target = latestT - 24 * 3600 * 1000;
    let baseline = snaps[0], best = Infinity;
    for (let i = 0; i < snaps.length - 1; i++) {
      const diff = Math.abs(new Date(snaps[i].timestamp).getTime() - target);
      if (diff < best) { best = diff; baseline = snaps[i]; }
    }
    const spanH = Math.max(1, Math.round((latestT - new Date(baseline.timestamp).getTime()) / 3600000));
    const label = spanH >= 48 ? `· ${Math.round(spanH / 24)}d` : `· ${spanH}h`;
    return {
      subscriber_count: (latest.subscriber_count || 0) - (baseline.subscriber_count || 0),
      view_count: (latest.view_count || 0) - (baseline.view_count || 0),
      video_count: (latest.video_count || 0) - (baseline.video_count || 0),
      label,
    };
  }, [snapshots]);

  if (booting) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <CircleNotchIcon size={40} className="text-[#16C784] spin-slow" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      <TopNav
        channels={channels}
        activeId={activeId}
        onSwitch={handleSwitch}
        onAddChannel={() => setAdding(true)}
        onDisconnect={handleDisconnect}
        onSync={handleSync}
        syncing={syncing}
        showSync={!showConnect}
      />

      {showAdminHome ? (
        <AdminHome onConnectOwn={() => setAdminConnect(true)} />
      ) : showConnect ? (
        <ConnectPanel
          onConnect={handleConnect}
          connecting={connecting}
          onCancel={
            adminConnect
              ? () => setAdminConnect(false)
              : channels.length > 0
              ? () => setAdding(false)
              : undefined
          }
        />
      ) : (
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 md:py-8">
          {/* View switcher */}
          <div className="flex items-center gap-1 border border-[#222226] w-fit mb-6" data-testid="view-switcher">
            {[
              { id: "single", label: "Channel", icon: GaugeIcon },
              { id: "overview", label: "All Channels", icon: SquaresFourIcon },
              ...(channels.length >= 2 ? [{ id: "compare", label: "Compare", icon: ScalesIcon }] : []),
              { id: "fsp", label: "FSP", icon: RobotIcon },
              { id: "radar", label: "Radar", icon: CrosshairIcon },
              { id: "usage", label: "Usage", icon: ChartBarIcon },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`view-tab-${t.id}`}
                onClick={() => setView(t.id)}
                className={`flex items-center gap-1.5 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 ${view === t.id ? "bg-[#16C784] text-black" : "text-[#A0A0AB] hover:text-white"}`}
              >
                <t.icon size={14} weight="fill" /> {t.label}
              </button>
            ))}
          </div>

          {view === "overview" ? (
            <ChannelsOverview channels={overview.channels} loading={overview.loading} activeId={activeId} onSelect={openChannel} />
          ) : view === "compare" ? (
            <CompareChannels channels={overview.channels} loading={overview.loading} activeId={activeId} />
          ) : view === "fsp" ? (
            activeId ? <FSPPanel channelId={activeId} /> : (
              <div className="flex items-center justify-center py-32"><CircleNotchIcon size={36} className="text-[#16C784] spin-slow" /></div>
            )
          ) : view === "radar" ? (
            activeId ? <CompetitorRadar key={`radar-${activeId}`} channelId={activeId} /> : (
              <div className="flex items-center justify-center py-32"><CircleNotchIcon size={36} className="text-[#16C784] spin-slow" /></div>
            )
          ) : view === "usage" ? (
            <UsageDashboard />
          ) : !channel ? (
            <div className="flex items-center justify-center py-32">
              <CircleNotchIcon size={36} className="text-[#16C784] spin-slow" />
            </div>
          ) : (
            <>
              {/* Channel header */}
          <motion.div
            key={activeId}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            {channel.thumbnail && (
              <img src={channel.thumbnail} alt={channel.title} referrerPolicy="no-referrer"
                className="w-16 h-16 rounded-full border-2 border-[#16C784] arc-glow object-cover" />
            )}
            <div>
              <h1 data-testid="channel-title" className="font-rajdhani font-bold text-2xl md:text-3xl tracking-wide text-white">
                {channel.title}
              </h1>
              <div className="font-mono text-xs text-[#5a5a63] mt-0.5">
                {channel.handle ? `${channel.handle} · ` : ""}Since {formatDate(channel.published_at)}
              </div>
            </div>
          </motion.div>

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <StatCard label="Subscribers" value={channel.subscriber_count} icon={UsersThreeIcon} accent="#A3E635" index={0} sub="FOLLOWERS" delta={dayDelta?.subscriber_count} deltaLabel={dayDelta?.label} />
            <StatCard label="Total Views" value={channel.view_count} icon={EyeIcon} accent="#16C784" index={1} sub={formatFull(channel.view_count)} delta={dayDelta?.view_count} deltaLabel={dayDelta?.label} />
            <StatCard label="Videos" value={channel.video_count} icon={FilmSlateIcon} accent="#FFFFFF" index={2} sub="PUBLISHED" delta={dayDelta?.video_count} deltaLabel={dayDelta?.label} />
          </div>

          {/* Channel diagnostic */}
          <div className="mb-4">
            <ChannelAnalysis key={`ca-${activeId}`} initial={channel.channel_analysis} videos={videos} channelId={activeId} />
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2">
              <PerformanceChart videos={videos} snapshots={snapshots} />
            </div>
            <div className="lg:col-span-1">
              <AISuggestions key={`sg-${activeId}`} initial={channel.ai_suggestions} channelId={activeId} videos={videos} />
            </div>
          </div>

          {/* Thumbnail insights */}
          <div className="mb-4">
            <ThumbnailInsights key={`th-${activeId}`} initial={channel.thumbnail_insights} channelId={activeId} />
          </div>

          {/* Video list */}
          <VideoList videos={videos} onAnalyzed={handleAnalyzed} onPromotedChange={handlePromotedChange} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
